import Link from 'next/link'
import { forwardRef } from 'react'

/**
 * Props for the MobileLink component
 */
interface MobileLinkProps {
  /** URL path for the link */
  href: string
  /** Callback function when link is clicked */
  onClick: () => void
  /** Link content */
  children: React.ReactNode
  /** Whether this is a featured link with special styling */
  featured?: boolean
  /** Red link styling for promotional links like Sale */
  highlighted?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Mobile navigation link component
 * Provides consistent styling and behavior for mobile menu links
 */
export const MobileLink = forwardRef<HTMLAnchorElement, MobileLinkProps>(
  ({ href, onClick, children, featured = false, highlighted = false, className = '' }, ref) => {
    const baseClasses = 'block py-2.5 px-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-vi-red focus:ring-offset-2'
    const featuredClasses = 'text-vi-gray hover:bg-gray-50 hover:text-vi-red font-medium'
    const highlightedClasses =
      'text-vi-red font-bold hover:text-red-800 hover:underline hover:bg-transparent'
    const defaultClasses = 'text-vi-red hover:text-red-600 hover:bg-red-50'

    const styleClasses = highlighted
      ? highlightedClasses
      : featured
        ? featuredClasses
        : defaultClasses

    const linkClasses = `${baseClasses} ${styleClasses} ${className}`.trim()

    return (
      <Link
        ref={ref}
        href={href}
        prefetch={false}
        onClick={onClick}
        className={linkClasses}
        role="menuitem"
      >
        {children}
      </Link>
    )
  }
)

MobileLink.displayName = 'MobileLink'
